import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Masonry from 'react-masonry-css';
import { 
  LockClosedIcon, 
  CameraIcon,
  CheckBadgeIcon,
  HeartIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlayIcon,
  CloudArrowUpIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import axios from 'axios';
import toast from 'react-hot-toast';
import { resolveImageUrl } from '../utils/image';
import GoogleDriveVideoEmbed from '../components/common/GoogleDriveVideoEmbed';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3500/api/v1';

const ProjectGalleryPage: React.FC = () => {
    const { projectId } = useParams<{ projectId: string }>();
    const [searchParams] = useSearchParams();
    const queryPassword = searchParams.get('password') || '';

    const [password, setPassword] = useState(queryPassword);
    const [authenticated, setAuthenticated] = useState(false);
    const [project, setProject] = useState<any>(null);
    const [images, setImages] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [fetchingMore, setFetchingMore] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    const [passInput, setPassInput] = useState(queryPassword);
    const [isUnpublished, setIsUnpublished] = useState(false);
    const [selectedImageForView, setSelectedImageForView] = useState<any>(null);

    const userStr = localStorage.getItem('user');
    const currentUser = userStr ? JSON.parse(userStr) : null;
    const isPhotographer = currentUser && project && currentUser.id === project.owner_id;

    const fetchGallery = async (pwd: string = password, pageNum: number = 1, append: boolean = false) => {
        try {
            if (pageNum === 1) {
                setLoading(true);
            } else {
                setFetchingMore(true);
            }

            const projectRes = await axios.get(`${API_BASE}/public/crm/projects/${projectId}?password=${pwd}`);
            setProject(projectRes.data.data.project);
            
            const imagesRes = await axios.get(`${API_BASE}/public/crm/projects/${projectId}/gallery?password=${pwd}&page=${pageNum}&limit=20`);
            const newImages = imagesRes.data.data.images || [];
            
            if (append) {
                setImages(prev => [...prev, ...newImages]);
            } else {
                setImages(newImages);
            }
            
            setHasMore(imagesRes.data.data.has_more);
            setTotalCount(imagesRes.data.data.total);
            setAuthenticated(true);
            setPassword(pwd);
            setPage(pageNum);
        } catch (err: any) {
            if (err.response?.status === 401) {
                setAuthenticated(false);
            } else if (err.response?.status === 403 && err.response?.data?.error === 'Gallery is not published yet') {
                setIsUnpublished(true);
            } else {
                toast.error('Failed to load gallery');
            }
        } finally {
            setLoading(false);
            setFetchingMore(false);
        }
    };

    useEffect(() => {
        fetchGallery(queryPassword || password);
    }, [projectId, queryPassword]);

    // Infinite scroll listener
    useEffect(() => {
        const handleScroll = () => {
            if (
                window.innerHeight + document.documentElement.scrollTop 
                >= document.documentElement.offsetHeight - 800 && // 800px from bottom
                !fetchingMore && 
                hasMore && 
                authenticated
            ) {
                fetchGallery(password, page + 1, true);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [fetchingMore, hasMore, page, authenticated, password, projectId]);

    const handleLogin = (e: React.FormEvent) => {
        e.preventDefault();
        fetchGallery(passInput, 1, false);
    };

    const toggleSelection = async (imageId: string, e?: React.MouseEvent) => {
        if (e) {
            e.stopPropagation();
        }
        try {
            let updatedImage: any;
            if (isPhotographer) {
                const res = await axios.put(`${API_BASE}/crm/gallery/selection/${imageId}`, {}, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                });
                updatedImage = res.data.data.image;
            } else {
                const res = await axios.put(`${API_BASE}/public/crm/gallery/selection/${imageId}`);
                updatedImage = res.data.data.image;
            }

            setImages(prev => prev.map(img => 
                img.id === imageId ? { 
                    ...img, 
                    is_selected: isPhotographer ? img.is_selected : (updatedImage?.is_selected ?? !img.is_selected),
                    is_photographer_selected: isPhotographer ? (updatedImage?.is_photographer_selected ?? !img.is_photographer_selected) : img.is_photographer_selected
                } : img
            ));
            // Also sync the lightbox view if this image is currently open
            if (selectedImageForView?.id === imageId) {
                setSelectedImageForView((prev: any) => prev ? { 
                    ...prev, 
                    is_selected: isPhotographer ? prev.is_selected : (updatedImage?.is_selected ?? !prev.is_selected),
                    is_photographer_selected: isPhotographer ? (updatedImage?.is_photographer_selected ?? !prev.is_photographer_selected) : prev.is_photographer_selected
                } : prev);
            }
        } catch (err) {
            toast.error('Failed to update selection');
        }
    };

    const handleSubmitSelection = async () => {
        try {
            if (isPhotographer) {
                await axios.post(`${API_BASE}/crm/projects/${projectId}/gallery/submit-photographer`, {}, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                });
                toast.success('Client notified of your selections!');
            } else {
                await axios.post(`${API_BASE}/public/crm/projects/${projectId}/gallery/submit${password ? `?password=${password}` : ''}`);
                toast.success('Photographer notified of your selections!');
            }
        } catch (err: any) {
            toast.error(err.response?.data?.error || 'Failed to submit selections');
        }
    };

    const navigateGallery = useCallback((direction: 'next' | 'prev') => {
        if (!selectedImageForView || images.length === 0) return;
        
        const currentIndex = images.findIndex(img => img.id === selectedImageForView.id);
        let nextIndex;
        
        if (direction === 'next') {
            nextIndex = (currentIndex + 1) % images.length;
        } else {
            nextIndex = (currentIndex - 1 + images.length) % images.length;
        }
        
        setSelectedImageForView(images[nextIndex]);
    }, [selectedImageForView, images]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!selectedImageForView) return;
            
            if (e.key === 'ArrowRight') navigateGallery('next');
            if (e.key === 'ArrowLeft') navigateGallery('prev');
            if (e.key === 'Escape') setSelectedImageForView(null);
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedImageForView, navigateGallery]);

    if (loading && !authenticated) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
            </div>
        );
    }

    if (isUnpublished) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center p-6">
                <motion.div 
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="max-w-md w-full text-center"
                >
                    <div className="w-16 h-16 bg-gray-50 rounded-3xl flex items-center justify-center mx-auto mb-6">
                        <CameraIcon className="w-8 h-8 text-gray-400" />
                    </div>
                    <h1 className="text-2xl font-black text-gray-900 uppercase tracking-tight mb-2">Not Published</h1>
                    <p className="text-gray-500 text-sm font-medium mb-8">This gallery is not published yet. Please check back later or contact your photographer.</p>
                </motion.div>
            </div>
        );
    }

    if (!authenticated) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center p-6">
                <motion.div 
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="max-w-md w-full text-center"
                >
                    <div className="w-16 h-16 bg-gray-50 rounded-3xl flex items-center justify-center mx-auto mb-6">
                        <LockClosedIcon className="w-8 h-8 text-gray-400" />
                    </div>
                    <h1 className="text-2xl font-black text-gray-900 uppercase tracking-tight mb-2">Private Gallery</h1>
                    <p className="text-gray-500 text-sm font-medium mb-8">This gallery is password protected. Please enter the access code provided by your photographer.</p>
                    
                    <form onSubmit={handleLogin} className="space-y-4">
                        <input 
                            type="password"
                            placeholder="Access Code"
                            value={passInput}
                            onChange={(e) => setPassInput(e.target.value)}
                            className="w-full px-6 py-4 bg-gray-50 rounded-2xl border-none focus:ring-2 focus:ring-black font-bold text-center tracking-widest"
                            autoFocus
                        />
                        <button 
                            type="submit"
                            className="w-full py-4 bg-black text-white rounded-2xl font-black uppercase tracking-widest text-xs hover:opacity-90 transition-all"
                        >
                            View Gallery
                        </button>
                    </form>
                </motion.div>
            </div>
        );
    }

    const selectedCount = images.filter(img => isPhotographer ? img.is_photographer_selected : img.is_selected).length;
    const isImageSelected = (id: string) => {
        const img = images.find(i => i.id === id);
        return isPhotographer ? img?.is_photographer_selected : img?.is_selected;
    };

    const breakpointColumnsObj = {
      default: 4,
      1440: 4,
      1100: 3,
      768: 2,
      500: 2
    };

    return (
        <div className="min-h-screen bg-white pb-20">
            <style>{`
                @media print {
                    @page { margin: 10mm; size: A4; }
                    .sticky { position: static !important; }
                    header { box-shadow: none !important; border-bottom: 2px solid #eee !important; margin-bottom: 2rem !important; }
                    .shadow-sm, .shadow-md, .shadow-lg, .shadow-xl, .shadow-2xl, .shadow, .ring-1 {
                        box-shadow: none !important;
                        ring: none !important;
                    }
                    .masonry-grid { display: flex !important; flex-wrap: wrap !important; }
                    .masonry-grid_column { width: 50% !important; padding: 5px !important; }
                    button, .fixed { display: none !important; }
                    footer { border-top: 1px solid #eee !important; }
                }
            `}</style>
            {isPhotographer && (
                <div className="bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-900/50 py-3 px-4 text-center">
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                        📸 You are viewing this gallery as the photographer. Selections you make will be saved as Photographer Picks.
                    </p>
                </div>
            )}
            {/* Header */}
            <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100 px-6 py-6 ring-1 ring-black/5 shadow-sm">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <div className="flex items-center space-x-3 mb-1">
                            <CameraIcon className="w-4 h-4 text-gray-400" />
                            <h2 className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">Proofing Gallery</h2>
                        </div>
                        <h1 className="text-2xl font-black text-gray-900 tracking-tight">{project?.title}</h1>
                    </div>
                    
                    <div className="flex items-center space-x-6">
                        <div className="text-right">
                            <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Selections</p>
                            <p className="text-lg font-black text-teal-600 leading-none mt-1">{selectedCount} / {totalCount}</p>
                        </div>
                        <button 
                            onClick={handleSubmitSelection}
                            className="px-8 py-3.5 bg-black text-white rounded-2xl font-black uppercase tracking-widest text-[10px] hover:scale-[1.02] active:scale-95 transition-all shadow-xl shadow-black/10"
                        >
                            {isPhotographer ? 'Submit Picks' : 'Submit Selections'}
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-[1600px] mx-auto px-4 md:px-6 mt-12">
                <Masonry
                    breakpointCols={breakpointColumnsObj}
                    className="masonry-grid"
                    columnClassName="masonry-grid_column"
                >
                    {images.map((img, i) => (
                        <motion.div 
                            key={img.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: (i % 20) * 0.05 }}
                            className="group relative rounded-[1rem] md:rounded-[2rem] overflow-hidden bg-gray-50 dark:bg-gray-800 ring-1 ring-black/5 cursor-zoom-in mb-4 md:mb-6 min-h-[140px]"
                            onClick={() => setSelectedImageForView(img)}
                        >
                            <img 
                                src={
                                    img.media_type === 'video'
                                        ? (img.thumbnail_signed || img.thumbnail || img.url_signed || 'https://cdn-icons-png.flaticon.com/512/3767/3767084.png')
                                        : resolveImageUrl(img.thumbnail_signed || img.thumbnail || img.url_signed || img.url)
                                } 
                                alt="" 
                                className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-110"
                                onError={(e) => {
                                    e.currentTarget.src = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 9' fill='%231f2937'><rect width='100%' height='100%'/><polygon points='7,3 10,4.5 7,6' fill='%23ffffff' opacity='0.3'/></svg>";
                                }}
                            />
                            
                            {img.media_type === 'video' && (
                                img.processing_status === 'processing' ? (
                                    // Processing badge
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/50 backdrop-blur-[2px]">
                                        <div className="flex flex-col items-center gap-2">
                                            <div className="w-8 h-8 rounded-full border-2 border-white/30 border-t-purple-400 animate-spin" />
                                            <span className="text-[9px] font-bold text-white/80 uppercase tracking-widest">Processing…</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/35 transition-all">
                                        <div className="p-3 md:p-4 bg-white/90 text-gray-900 rounded-full shadow-lg transition-transform group-hover:scale-110">
                                            <PlayIcon className="w-6 h-6 md:w-8 md:h-8" />
                                        </div>
                                    </div>
                                )
                            )}

                            <div className="absolute bottom-4 left-4 z-20 pointer-events-none">
                                <div className="bg-black/50 backdrop-blur-md px-3 py-1 rounded-xl">
                                    <p className="text-[10px] font-bold text-white truncate max-w-[150px]">
                                        {img.original_name || img.title || img.url?.split('/').pop()?.split('?')[0] || 'Media'}
                                    </p>
                                </div>
                            </div>
                            
                            {/* Watermark Overlay */}
                            <div className="absolute inset-0 pointer-events-none flex items-center justify-center opacity-10 select-none">
                                <span className="text-xl md:text-3xl lg:text-4xl font-black uppercase tracking-[0.5em] md:tracking-[1em] -rotate-45 text-black">PROOF</span>
                            </div>

                            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                            {/* Top-right controls: Photographer badge + Heart */}
                            <div className="absolute top-2 right-2 md:top-4 md:right-4 flex items-center space-x-1.5 z-10">
                                {img.is_photographer_selected && (
                                    <div className="flex items-center space-x-1 bg-black/70 backdrop-blur-sm text-white px-2 py-1.5 rounded-full pointer-events-none">
                                        <CameraIcon className="w-3 h-3 text-teal-400 flex-shrink-0" />
                                        <span className="text-[8px] font-black uppercase tracking-wider text-teal-300 hidden sm:inline whitespace-nowrap">Photographer's Pick</span>
                                    </div>
                                )}
                                <button 
                                    onClick={(e) => toggleSelection(img.id, e)}
                                    className={`p-2 md:p-2.5 rounded-full transition-all active:scale-90 flex-shrink-0 ${
                                        isImageSelected(img.id) 
                                            ? 'bg-red-500 text-white shadow-lg shadow-red-500/30' 
                                            : 'bg-white/20 text-white backdrop-blur-md hover:bg-white/40'
                                    }`}
                                >
                                    {isImageSelected(img.id) ? (
                                        <HeartIconSolid className="w-4 h-4 md:w-5 md:h-5" />
                                    ) : (
                                        <HeartIcon className="w-4 h-4 md:w-5 md:h-5" />
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    ))}
                </Masonry>

                {fetchingMore ? (
                    <div className="flex justify-center py-10">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
                    </div>
                ) : !hasMore && images.length > 0 && (
                    <div className="text-center py-10 text-gray-400 font-bold uppercase tracking-widest text-[10px]">
                        You've reached the end of the gallery
                    </div>
                )}

                {images.length === 0 && !loading && (
                    <div className="py-40 text-center">
                        <CameraIcon className="w-12 h-12 text-gray-200 mx-auto mb-4" />
                        <h2 className="text-xl font-black text-gray-900 uppercase tracking-tight">No photos yet</h2>
                        <p className="text-gray-400 font-medium">Your photographer is still processing the session photos.</p>
                    </div>
                )}
            </main>

            {/* Full View Lightbox */}
            <AnimatePresence>
                {selectedImageForView && (
                    <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 md:p-10"
                        onClick={() => setSelectedImageForView(null)}
                    >
                        <motion.div 
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            className="relative max-w-[90vw] max-h-[85vh] flex items-center justify-center"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {selectedImageForView.media_type === 'video' ? (
                                selectedImageForView.processing_status === 'processing' ? (
                                    // Video is still being processed by Google Drive
                                    <div className="relative w-full max-w-4xl aspect-video rounded-2xl overflow-hidden shadow-2xl bg-black/80 border border-white/10 z-10 flex flex-col items-center justify-center gap-4 px-8 text-center">
                                        <div className="w-14 h-14 rounded-full border-4 border-white/20 border-t-purple-400 animate-spin" />
                                        <p className="text-white text-lg font-bold">Video is being processed</p>
                                        <p className="text-white/50 text-sm">Google Drive is still processing this video. Please check back in a few minutes.</p>
                                    </div>
                                ) : (
                                    <div className="relative w-full max-w-4xl aspect-video rounded-2xl overflow-hidden shadow-2xl bg-black border border-white/10 z-10">
                                        {(selectedImageForView.video_platform === 'google_drive' || (selectedImageForView.url && selectedImageForView.url.includes('drive.google.com'))) ? (
                                            <GoogleDriveVideoEmbed
                                                src={selectedImageForView.url}
                                                title={selectedImageForView.title || "Video Preview"}
                                                allowDownload={true}
                                            />
                                        ) : (
                                            <iframe
                                                src={(() => {
                                                    const videoUrl = selectedImageForView.url;
                                                    const platform = selectedImageForView.video_platform;
                                                    if (platform === 'vimeo') {
                                                        try {
                                                            const urlObj = new URL(videoUrl);
                                                            const parts = urlObj.pathname.split('/').filter(Boolean);
                                                            if (parts.length > 0) return `https://player.vimeo.com/video/${parts[parts.length - 1]}?autoplay=1`;
                                                        } catch (e) {}
                                                    } else if (platform === 'instagram') {
                                                        try {
                                                            const urlObj = new URL(videoUrl);
                                                            const parts = urlObj.pathname.split('/').filter(Boolean);
                                                            for (let i = 0; i < parts.length; i++) {
                                                                if ((parts[i] === 'reel' || parts[i] === 'p' || parts[i] === 'reels') && i + 1 < parts.length) {
                                                                    const pathType = parts[i] === 'reels' ? 'reel' : parts[i];
                                                                    return `https://www.instagram.com/${pathType}/${parts[i+1]}/embed/`;
                                                                }
                                                            }
                                                        } catch (e) {}
                                                    } else {
                                                        try {
                                                            const urlObj = new URL(videoUrl);
                                                            if (urlObj.hostname.toLowerCase() === 'youtu.be') return `https://www.youtube.com/embed/${urlObj.pathname.slice(1)}?autoplay=1`;
                                                            const v = urlObj.searchParams.get('v');
                                                            if (v) return `https://www.youtube.com/embed/${v}?autoplay=1`;
                                                            if (urlObj.pathname.startsWith('/embed/')) return `https://www.youtube.com/embed/${urlObj.pathname.slice(7)}?autoplay=1`;
                                                            if (urlObj.pathname.startsWith('/shorts/')) return `https://www.youtube.com/embed/${urlObj.pathname.slice(8)}?autoplay=1`;
                                                        } catch (e) {}
                                                    }
                                                    return videoUrl;
                                                })()}
                                                title={selectedImageForView.title || "Video Preview"}
                                                frameBorder="0"
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                allowFullScreen
                                                className="absolute inset-0 w-full h-full border-0 rounded-2xl"
                                            ></iframe>
                                        )}
                                    </div>
                                )
                            ) : (
                                <img 
                                    src={resolveImageUrl(selectedImageForView.url_signed || selectedImageForView.url)} 
                                    alt="" 
                                    className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl select-none"
                                />
                            )}
                            
                            {/* Watermark — images only; hidden for video to avoid covering the player */}
                            {selectedImageForView.media_type !== 'video' && (
                                <div className="absolute inset-0 pointer-events-none flex items-center justify-center opacity-30 select-none">
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-10 md:gap-20 -rotate-45">
                                        <span className="text-3xl md:text-7xl font-black uppercase tracking-[0.5em] text-white/40">PROOF</span>
                                        <span className="text-3xl md:text-7xl font-black uppercase tracking-[0.5em] text-white/40">PROOF</span>
                                        <span className="text-3xl md:text-7xl font-black uppercase tracking-[0.5em] text-white/40">PROOF</span>
                                        <span className="text-3xl md:text-7xl font-black uppercase tracking-[0.5em] text-white/40">PROOF</span>
                                        <span className="text-3xl md:text-7xl font-black uppercase tracking-[0.5em] text-white/40">PROOF</span>
                                        <span className="text-3xl md:text-7xl font-black uppercase tracking-[0.5em] text-white/40">PROOF</span>
                                    </div>
                                </div>
                            )}

                            {/* Navigation Buttons */}
                            <button 
                                onClick={(e) => { e.stopPropagation(); navigateGallery('prev'); }}
                                className="absolute left-2 md:left-10 p-3 md:p-4 bg-white/10 hover:bg-white/20 text-white rounded-full backdrop-blur-md transition-all group active:scale-90"
                            >
                                <ChevronLeftIcon className="w-6 h-6 md:w-8 md:h-8 group-hover:-translate-x-1 transition-transform" />
                            </button>
                            <button 
                                onClick={(e) => { e.stopPropagation(); navigateGallery('next'); }}
                                className="absolute right-2 md:right-10 p-3 md:p-4 bg-white/10 hover:bg-white/20 text-white rounded-full backdrop-blur-md transition-all group active:scale-90"
                            >
                                <ChevronRightIcon className="w-6 h-6 md:w-8 md:h-8 group-hover:translate-x-1 transition-transform" />
                            </button>

                            <div className="absolute top-4 right-4 md:top-6 md:right-6 flex items-center space-x-3 md:space-x-4">
                                <a 
                                    href={(() => {
                                        const item = selectedImageForView;
                                        if (item.video_platform === 'google_drive') {
                                            let fileId = "";
                                            try {
                                                const urlObj = new URL(item.url);
                                                const pathParts = urlObj.pathname.split('/').filter(Boolean);
                                                for (let i = 0; i < pathParts.length; i++) {
                                                    if (pathParts[i] === 'd' && i + 1 < pathParts.length) {
                                                        fileId = pathParts[i+1];
                                                        break;
                                                    }
                                                }
                                                if (!fileId) {
                                                    const idParam = urlObj.searchParams.get('id');
                                                    if (idParam) fileId = idParam;
                                                }
                                            } catch (e) {}
                                            if (fileId) {
                                                return `https://drive.google.com/uc?export=download&id=${fileId}`;
                                            }
                                        }
                                        return item.url_signed || item.url;
                                    })()}
                                    download={selectedImageForView.original_name || "download"}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-3 md:p-4 bg-white/10 text-white backdrop-blur-md rounded-full hover:bg-white/20 transition-all active:scale-90 flex items-center justify-center"
                                    title="Download File"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <CloudArrowUpIcon className="w-5 h-5 md:w-6 md:h-6 rotate-180" />
                                </a>
                                <button 
                                    onClick={(e) => toggleSelection(selectedImageForView.id, e)}
                                    className={`p-3 md:p-4 rounded-full transition-all active:scale-90 ${
                                        isImageSelected(selectedImageForView.id)
                                            ? 'bg-red-500 text-white shadow-xl shadow-red-500/40' 
                                            : 'bg-white/10 text-white backdrop-blur-md hover:bg-white/20'
                                    }`}
                                >
                                    {isImageSelected(selectedImageForView.id) ? (
                                        <HeartIconSolid className="w-5 h-5 md:w-6 md:h-6" />
                                    ) : (
                                        <HeartIcon className="w-5 h-5 md:w-6 md:h-6" />
                                    )}
                                </button>
                                <button 
                                    onClick={() => setSelectedImageForView(null)}
                                    className="p-3 md:p-4 bg-white/10 text-white backdrop-blur-md rounded-full hover:bg-white/20 transition-all active:scale-90"
                                >
                                    <XMarkIcon className="w-5 h-5 md:w-6 md:h-6" />
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <footer className="mt-20 border-t border-gray-100 py-12 px-6 text-center">
                <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Powered by ClickCollab CRM</p>
            </footer>
        </div>
    );
};

export default ProjectGalleryPage;
